// @mui
import { Box, Button, Tab, Tabs, Typography, useTheme } from '@mui/material';
// components
import { CenteredContent } from 'theme/elements/Content';
import { useNavigate } from 'react-router-dom';
import { pagesEnum } from 'constants/ui.constants';
import { Stack } from '@mui/system';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { useAppSelector } from 'hooks/redux.hooks';
import { CustomAppBar } from 'theme/layouts/navbar';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { ButtonComponent } from 'components/ButtonComponent';
import { FormHeader3 } from 'theme/elements/Headers';
import { Page } from 'theme/elements/Page';
import { useAppDispatch } from 'hooks/redux.hooks';
import { getNewAccounts } from 'services/new_accounts.service';
import { getAccountsInProcess, resetNewAccount, setNewAccount } from 'state/reducers/new_accounts.reducers';
import { AccountsInProcessTable } from './AccountsInProcessTable';
import { WaitingForApprovalTable } from './WaitingForApprovalTable';
import { DeclinedAccountsTable } from './DeclinedAccountsTable';
import { NewAccountStatus } from 'constants/new_accounts.constants';
import { hasPermission } from 'components/AuthorizationComponent';
import { permissionEnums } from 'constants/permission.constants';
import _, { isArray } from 'lodash';

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

export function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && <Box sx={{ p: 3 }}>{children}</Box>}
		</div>
	);
}

export const PendingAccounts = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const t = useAppSelector(selectTranslations);
	const accountsInProcess = useAppSelector(getAccountsInProcess);
	const dispatch = useAppDispatch();
	const pagePermission = hasPermission([permissionEnums.READ_ACCOUNT]);

	useEffect(() => {
		dispatch(resetNewAccount());
		if (pagePermission) {
			dispatch(getNewAccounts(true));
		}
	}, [pagePermission]);

	const [value, setValue] = useState(0);

	const handleChange = (event: React.SyntheticEvent, newValue: number) => {
		setValue(newValue);
	};

	const draftAccounts = useMemo(() => {
		if (_.has(accountsInProcess, NewAccountStatus.DRAFT)) {
			return [
				...accountsInProcess[NewAccountStatus.DRAFT],
				..._.get(accountsInProcess, NewAccountStatus.DENIED, []),
			];
		}
		return [];
	}, [accountsInProcess]);

	const inReviewCount = useMemo(() => {
		const inReviewAccounts = accountsInProcess?.[NewAccountStatus.IN_REVIEW];
		return isArray(inReviewAccounts) ? inReviewAccounts.length : 0;
	}, [accountsInProcess]);

	return (
		<Page>
			<Fragment>
				<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
					<ButtonComponent
						disabled={false}
						handleClick={() => {
							//dispatch(resetNewAccount());
							navigate(pagesEnum.NEW_ACCOUNT + '/create');
						}}
						text={`${t('page.accounts.new_account.add_customer')}`}
						icon={
							<img
								src={'/icons/plus-sign.svg'}
								style={{
									marginLeft: '10px',
								}}
							/>
						}
						styleObj={{ flexDirection: 'row-reverse' }}
					/>
				</Box>

				<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
					<Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
						<Tab label={t('page.accounts.accounts_in_process.in_process_tab')} />
						<Tab
							label={
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									<span>{t('page.accounts.accounts_in_process.waiting_for_approval_tab')}</span>
									{inReviewCount > 0 && (
										<Box
											sx={{
												minWidth: 20,
												height: 20,
												px: 0.5,
												borderRadius: '50%',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												backgroundColor: theme.palette.root.orange_3,
											}}
										>
											<Typography
												component="span"
												sx={{
													color: `${theme.palette.root.basicWhite} !important`,
													fontSize: 12,
													fontWeight: 700,
													lineHeight: 1,
												}}
											>
												{inReviewCount}
											</Typography>
										</Box>
									)}
								</Box>
							}
						/>
						<Tab label={t('page.accounts.accounts_in_process.rejected_tab')} />
					</Tabs>
				</Box>
				<TabPanel value={value} index={0}>
					<FormHeader3 title={t('page.accounts.accounts_in_process.in_process_description')} />
					<AccountsInProcessTable draftAccounts={draftAccounts} />
				</TabPanel>
				<TabPanel value={value} index={1}>
					<FormHeader3 title={t('page.accounts.accounts_in_process.waiting_for_approval_description')} />
					<WaitingForApprovalTable inReviewAccounts={accountsInProcess[NewAccountStatus.IN_REVIEW]} />
				</TabPanel>
				<TabPanel value={value} index={2}>
					<DeclinedAccountsTable declinedAccounts={accountsInProcess[NewAccountStatus.DENIED]} />
				</TabPanel>
			</Fragment>
		</Page>
	);
};
